import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, ExternalLink, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PageTemplate from '../../components/Common/PageTemplate';

const Museums = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('about');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };

  return (
    <PageTemplate
      title={t('pages.museums.title')}
      subtitle={t('pages.museums.subtitle')}
    >
      <motion.div
        className="container mx-auto px-4 py-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* HUZOM - Hacettepe Üniversitesi Zooloji Müzesi */}
        <motion.div
          variants={itemVariants}
          className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl shadow-2xl overflow-hidden mb-16 border border-gray-100"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl -z-0"></div>

          <div className="relative z-10 p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                <Sparkles className="text-white" size={28} />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {t('pages.museums.huzom.title')}
              </h2>
            </div>

            <p className="text-gray-700 text-lg leading-relaxed mb-8 max-w-4xl">
              {t('pages.museums.huzom.description')}
            </p>
          </div>

          {/* Responsive YouTube Video Embed */}
          <div className="relative w-full px-8 md:px-12 pb-8 md:pb-12">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/DZb9uRBOc6g"
                title="Türlerin dünyasına Hacettepe'de adım at"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </motion.div>

        {/* Biyosfer Müzesi */}
        <motion.div
          variants={itemVariants}
          className="relative bg-gradient-to-br from-green-50 via-white to-emerald-50 rounded-3xl shadow-2xl overflow-hidden border border-gray-100"
        >
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-green-400/10 to-emerald-400/10 rounded-full blur-3xl -z-0"></div>

          <div className="relative z-10 p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
                <Sparkles className="text-white" size={28} />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {t('pages.museums.biosphere.title')}
              </h2>
            </div>

            {/* Image Gallery */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <motion.div
                whileHover={{ scale: 1.05, y: -10 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                <img
                  src="https://biyosfermuze.hacettepe.edu.tr/wp-content/uploads/2022/10/05_E-HacettepeBCM_IMG_4666_x_midi-370x210.jpg"
                  alt="Biyosfer Müzesi"
                  className="w-full h-56 object-cover rounded-2xl shadow-lg ring-2 ring-gray-100 group-hover:ring-green-300 transition-all duration-300"
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, y: -10 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                <img
                  src="https://biyosfermuze.hacettepe.edu.tr/wp-content/uploads/2022/10/01_Yercekim-HacettepeBCM-DSCF5864_x_midi-3-scaled-1-300x207.jpg"
                  alt="Biyosfer Müzesi İç Mekan"
                  className="w-full h-56 object-cover rounded-2xl shadow-lg ring-2 ring-gray-100 group-hover:ring-green-300 transition-all duration-300"
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, y: -10 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                <img
                  src="https://biyosfermuze.hacettepe.edu.tr/wp-content/uploads/2022/10/DSCF6389-370x210.jpg"
                  alt="Biyosfer Müzesi Koleksiyon"
                  className="w-full h-56 object-cover rounded-2xl shadow-lg ring-2 ring-gray-100 group-hover:ring-green-300 transition-all duration-300"
                />
              </motion.div>
            </div>

            {/* Tabs Navigation */}
            <div className="flex flex-wrap gap-3 mb-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-8 py-4 font-semibold rounded-xl transition-all duration-300 ${
                  activeTab === 'about'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/50'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                }`}
                onClick={() => setActiveTab('about')}
              >
                {t('pages.museums.biosphere.tabs.about')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-8 py-4 font-semibold rounded-xl transition-all duration-300 ${
                  activeTab === 'vision'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/50'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                }`}
                onClick={() => setActiveTab('vision')}
              >
                {t('pages.museums.biosphere.tabs.vision')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-8 py-4 font-semibold rounded-xl transition-all duration-300 ${
                  activeTab === 'mission'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/50'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                }`}
                onClick={() => setActiveTab('mission')}
              >
                {t('pages.museums.biosphere.tabs.mission')}
              </motion.button>
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="mb-12 bg-white/50 backdrop-blur-sm rounded-2xl p-8 shadow-lg"
              >
                {activeTab === 'about' && (
                  <div className="text-gray-700 leading-relaxed space-y-6 text-lg">
                    <p className="first-letter:text-5xl first-letter:font-bold first-letter:text-green-600 first-letter:mr-2 first-letter:float-left">
                      {t('pages.museums.biosphere.about.paragraph1')}
                    </p>
                    <p>{t('pages.museums.biosphere.about.paragraph2')}</p>
                    <p>{t('pages.museums.biosphere.about.paragraph3')}</p>
                  </div>
                )}
                {activeTab === 'vision' && (
                  <div className="text-gray-700 leading-relaxed space-y-6 text-lg">
                    <p className="font-bold text-xl text-green-700 first-letter:text-5xl first-letter:text-green-600">
                      {t('pages.museums.biosphere.vision.intro')}
                    </p>
                    <p>{t('pages.museums.biosphere.vision.paragraph1')}</p>
                    <p>{t('pages.museums.biosphere.vision.paragraph2')}</p>
                  </div>
                )}
                {activeTab === 'mission' && (
                  <div className="text-gray-700 leading-relaxed space-y-6 text-lg">
                    <p className="first-letter:text-5xl first-letter:font-bold first-letter:text-green-600 first-letter:mr-2 first-letter:float-left">
                      {t('pages.museums.biosphere.mission.paragraph1')}
                    </p>
                    <p>{t('pages.museums.biosphere.mission.paragraph2')}</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 shadow-xl border border-green-100"
            >
              <h3 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-8">
                {t('pages.museums.biosphere.contact.title')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                    <MapPin className="text-white" size={22} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 mb-1">{t('pages.museums.biosphere.contact.addressLabel')}</p>
                    <p className="text-gray-600 text-sm">{t('pages.museums.biosphere.contact.address')}</p>
                  </div>
                </motion.div>
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                    <Phone className="text-white" size={22} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 mb-1">{t('pages.museums.biosphere.contact.phoneLabel')}</p>
                    <p className="text-gray-600 text-sm">0-(312)-297-6503</p>
                  </div>
                </motion.div>
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                    <Mail className="text-white" size={22} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 mb-1">{t('pages.museums.biosphere.contact.emailLabel')}</p>
                    <a href="mailto:biyosfermuze@hacettepe.edu.tr" className="text-green-600 hover:text-green-700 font-medium text-sm hover:underline">
                      biyosfermuze@hacettepe.edu.tr
                    </a>
                  </div>
                </motion.div>
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                    <ExternalLink className="text-white" size={22} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 mb-1">{t('pages.museums.biosphere.contact.websiteLabel')}</p>
                    <a
                      href="https://biyosfermuze.hacettepe.edu.tr/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700 font-medium text-sm hover:underline"
                    >
                      biyosfermuze.hacettepe.edu.tr
                    </a>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </PageTemplate>
  );
};

export default Museums;
